import { useState } from 'react';
import { SEO } from '@/components/base/SEO';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { Breadcrumb } from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const testimonials = [
    {
      name: '张教授',
      role: '东亚文化研究者',
      content: '韩国伦理网是我研究东亚伦理文化的重要参考资料。内容系统全面，分析深入透彻，特别是关于儒家思想在韩国现代化转型中的角色分析，令我受益匪浅。',
    },
    {
      name: '王女士',
      role: '韩企人力资源经理',
      content: '作为在韩企工作多年的HR，这个网站帮助我更深入地理解了韩国同事的行为逻辑和职场文化根源，极大地改善了我的跨文化管理能力。强烈推荐给所有在韩企工作的人。',
    },
    {
      name: '李明浩',
      role: '留学生',
      content: '赴韩留学前通过这个网站系统学习了韩国社会伦理，让我在韩国的学习和生活更加顺利。网站的内容既专业又易懂，是非常棒的韩国文化入门资源。',
    },
  ];

  const stats = [
    { value: '18', label: '深度文章' },
    { value: '5', label: '主题分类' },
    { value: '5', label: '专家作者' },
    { value: '10万+', label: '累计阅读' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector('[name="phone_alt"]') as HTMLInputElement;

    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('您的留言已提交成功，我们会尽快回复！');
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9qr4hue3oq4jqumtpd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快回复您。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试。';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩国伦理网',
    url: 'https://korean-ethics.com',
    description: '专注于韩国伦理文化研究与传播的知识平台',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@korean-ethics.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩国伦理网',
    description: '韩国伦理网致力于系统介绍和深度解析韩国伦理文化，涵盖儒家思想影响、现代道德观念、职场伦理及家庭伦理等核心领域。',
    url: 'https://korean-ethics.com/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <SEO
        title="关于我们 - 韩国伦理网"
        description="韩国伦理网致力于系统介绍和深度解析韩国伦理文化，涵盖儒家思想影响、现代道德观念、职场伦理及家庭伦理等核心领域。了解我们的品牌故事和专业团队。"
        keywords="韩国伦理,关于我们,韩国文化研究,伦理研究团队"
        canonicalUrl="https://korean-ethics.com/about"
        ogTitle="关于我们 - 韩国伦理网"
        ogDescription="韩国伦理网致力于系统介绍和深度解析韩国伦理文化。了解我们的品牌故事和专业团队。"
        ogType="website"
        ogUrl="https://korean-ethics.com/about"
      />

      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-8 md:py-12">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
              关于韩国伦理网
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed mb-12">
              我们是一群深耕东亚文化研究的学人和从业者，致力于搭建一座连接中韩伦理文化的知识桥梁。通过系统化的内容组织和深入浅出的解析，帮助华语读者全面理解韩国伦理体系的独特魅力与当代价值。
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 border border-background-200 rounded-lg p-5 text-center">
                  <div className="text-2xl md:text-3xl font-bold font-heading text-accent-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-600">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10 md:py-14 border-t border-background-200">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">
              我们的使命
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-4">
                  <i className="ri-book-open-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">知识传播</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  系统化梳理和传播韩国伦理文化知识，为华语读者提供全面、准确、深入的内容资源。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-4">
                  <i className="ri-global-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">文化桥梁</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  搭建中韩文化交流的桥梁，促进两国人民在伦理文化层面的相互理解与尊重。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-4">
                  <i className="ri-lightbulb-line text-lg"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">深度洞察</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  提供有深度的分析与评论，帮助读者把握韩国伦理发展的历史脉络与当代趋势。
                </p>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 border-t border-background-200">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 border-t border-background-200 pt-3">
                    <div className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center text-accent-700 text-xs font-bold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10 md:py-14 border-t border-background-200">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-8 max-w-2xl leading-relaxed">
              如果您对韩国伦理文化有任何疑问、建议或合作意向，欢迎通过以下表单与我们取得联系。我们会在48小时内回复您的留言。
            </p>

            <div className="max-w-2xl">
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-950 focus:outline-none focus:border-accent-400 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-950 focus:outline-none focus:border-accent-400 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <input
                    id="about-subject"
                    name="subject"
                    type="text"
                    className="w-full px-3 py-2 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-950 focus:outline-none focus:border-accent-400 transition-colors duration-200"
                    placeholder="请输入留言主题"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-950 focus:outline-none focus:border-accent-400 transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {formStatus !== 'idle' && (
                  <div
                    className={`text-sm p-3 rounded-md mb-4 ${
                      formStatus === 'success'
                        ? 'bg-secondary-100 text-secondary-800'
                        : formStatus === 'error'
                        ? 'bg-accent-100 text-accent-800'
                        : 'bg-background-100 text-foreground-700'
                    }`}
                  >
                    {formMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="px-6 py-2.5 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                </button>
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}